import moment from 'moment'
import type { TFunction } from 'i18next'
import type { PaymentSettingType, SalesDiscountType, SalesType } from '@/models/sales'
import type {
  PostClosingModalSubmitValues,
  PostClosingSalesAdjustmentParams,
  SelectablePaymentBusinessDateItem,
} from '@/models/post-closing-sales-adjustment'

export type PostClosingPaymentSource = Pick<SalesType, 'paymentAtUnixtime' | 'paymentUnixtime' | 'paymentBusinessSalesDate'>

export type PostClosingContextFields = {
  isConfirmed?: boolean | number | null
  isConfirm?: boolean | number | null
}

/** API の 0/1/null フラグを真偽値に（前受金行は null） */
export const isSalesFlagEnabled = (value: boolean | number | null | undefined): boolean => {
  return value === 1 || value === true
}

/**
 * 売上日・締め日の共通パース。
 * Date フォールバック（ブラウザ依存・deprecation）を避け、不正値は invalid のまま返す。
 * 空文字 / undefined も invalid（moment(undefined) が「今」になるのを防ぐ）。
 */
export const parseSalesDay = (value?: string | null): moment.Moment =>
  moment(value ?? '', ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)

/** confirmedDay 以前（当日含む）なら締め済み。パース不能時は false（全日締め扱いを避ける） */
export const isClosedSalesDate = (salesDate: string, confirmedDay: string): boolean => {
  if (!salesDate || !confirmedDay) return false
  const sales = parseSalesDay(salesDate)
  const confirmed = parseSalesDay(confirmedDay)
  if (!sales.isValid() || !confirmed.isValid()) return false
  return !sales.isAfter(confirmed, 'day')
}

type DiscountPaymentMaster = Pick<PaymentSettingType, 'id' | 'name'>

/** 割引の精算済判定。編集中は paymentMasters で ID→名前を解決し、'-' 以外を精算済とする。 */
export const resolveDiscountPaid = (
  discount: Pick<SalesDiscountType, 'salesPaymentName'> & Partial<Pick<SalesDiscountType, 'salesPaymentId'>>,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  if (paymentMasters?.length && discount.salesPaymentId) {
    const master = paymentMasters.find(payment => payment.id === discount.salesPaymentId)
    if (master) {
      return master.name !== '-'
    }
  }
  return discount.salesPaymentName !== '-' || !discount.salesPaymentName
}

/** 支払いマスタ変更時に salesPaymentName を同期（編集中の精算判定ズレ防止） */
export const syncDiscountPaymentNameFromMaster = (
  discount: Pick<SalesDiscountType, 'salesPaymentId' | 'salesPaymentName'>,
  paymentMasters: DiscountPaymentMaster[],
): void => {
  const master = paymentMasters.find(payment => payment.id === discount.salesPaymentId)
  if (master) {
    discount.salesPaymentName = master.name
  }
}

type PostClosingTargetSale = Pick<SalesType, 'isConfirm' | 'isPaid' | 'isConfirmed'>
type PostClosingTargetDiscount = Pick<SalesDiscountType, 'isConfirm' | 'isConfirmed' | 'salesPaymentName' | 'salesPaymentId'>
type FixedSalesDetail = PostClosingTargetSale & Pick<SalesType, 'hasSentPos'>
type FixedDiscountDetail = PostClosingTargetDiscount & Pick<SalesDiscountType, 'hasSentPos'>

/**
 * 締め後修正の対象条件（鉛筆 / post-closing API）。
 * `(isConfirm && isPaid) || isConfirmed`。hasSentPos は含めない。
 */
export const isPostClosingTarget = (
  item: PostClosingTargetSale | PostClosingTargetDiscount,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  const isPaid = 'isPaid' in item ? Boolean(item.isPaid) : resolveDiscountPaid(item, paymentMasters)
  return (isSalesFlagEnabled(item.isConfirm) && isPaid) || isSalesFlagEnabled(item.isConfirmed)
}

/** 固定明細のグレー表示。hasSentPos のみでもグレー固定（鉛筆対象にはしない） */
export const isFixedSalesDetail = (sale: FixedSalesDetail): boolean => {
  return isPostClosingTarget(sale) || Boolean(sale.hasSentPos)
}

/** 固定明細のグレー表示（割引）。hasSentPos のみでもグレー固定（鉛筆対象にはしない） */
export const isFixedDiscountDetail = (discount: FixedDiscountDetail, paymentMasters?: DiscountPaymentMaster[]): boolean => {
  return isPostClosingTarget(discount, paymentMasters) || Boolean(discount.hasSentPos)
}

/** 締め後修正ボタン（鉛筆）。権限は呼び出し側。 */
export const canShowPostClosingEditPencil = (
  item: PostClosingTargetSale | PostClosingTargetDiscount,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  return isPostClosingTarget(item, paymentMasters)
}

/** post-closing-adjustment API を叩いてよいか（BE isPostClosingContext と同趣旨） */
export const canUsePostClosingSalesApi = (
  item: PostClosingTargetSale | PostClosingTargetDiscount,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  return isPostClosingTarget(item, paymentMasters)
}

/** 締め後修正UI（灰色行）を表示するか。鉛筆の有無は canShowPostClosingEditPencil で別判定。 */
export const shouldShowPostClosingUsageDetailRow = (sale: FixedSalesDetail, isPostClosingEditingRow: boolean): boolean => {
  if (isFixedSalesDetail(sale)) return true
  return isPostClosingEditingRow
}

/** 部屋フィルター適用後の利用明細リストを origin から復元 */
export const filterUsageDetailsByGuestRoom = (sales: SalesType[], filterGuestRoomAssignId: string[] | undefined): SalesType[] => {
  if (!filterGuestRoomAssignId?.length) return sales.map(value => ({ ...value }))
  return sales.filter(sale => filterGuestRoomAssignId.includes(sale.guestRoomAssignId || '')).map(value => ({ ...value }))
}

export const confirmPostClosingClosedDate = (salesDate: string, confirmedDay: string, t: TFunction): boolean => {
  if (!isClosedSalesDate(salesDate, confirmedDay)) return true
  return window.confirm(
    t('HotelPms.PostClosing.closedDateConfirm', {
      salesDate: parseSalesDay(salesDate).format(t('YYYY-MM-DD')),
    }),
  )
}

/** ① 締め済み日付への変更時のみ確認（同一日付の再選択・締め前日への変更では表示しない） */
export const confirmClosedSalesDateSelection = (
  nextSalesDate: string,
  currentSalesDate: string | undefined,
  confirmedDay: string,
  t: TFunction,
): boolean => {
  if (!nextSalesDate || nextSalesDate === currentSalesDate) return true
  if (!isClosedSalesDate(nextSalesDate, confirmedDay)) return true
  return confirmPostClosingClosedDate(nextSalesDate, confirmedDay, t)
}

const parsePaymentUnixtimeSeconds = (value: number): moment.Moment | null => {
  if (!Number.isFinite(value) || value <= 0) return null
  const unixMoment = moment.unix(value)
  return unixMoment.isValid() ? unixMoment : null
}

/** 入金日時変更モーダル対象か。paymentAtUnixtime（正式キー）が有効なときのみ true。 */
export const hasPostClosingPaymentDatetime = (source: Pick<PostClosingPaymentSource, 'paymentAtUnixtime'>): boolean => {
  if (source.paymentAtUnixtime == null) return false
  return parsePaymentUnixtimeSeconds(source.paymentAtUnixtime) != null
}

/** BE 正式キー paymentAtUnixtime（Unix 秒）。無いときは null。 */
export const resolvePostClosingPaymentMoment = (source: PostClosingPaymentSource): moment.Moment | null => {
  if (source.paymentAtUnixtime != null) {
    const parsed = parsePaymentUnixtimeSeconds(source.paymentAtUnixtime)
    if (parsed) return parsed
  }

  if (source.paymentUnixtime != null) {
    return parsePaymentUnixtimeSeconds(source.paymentUnixtime)
  }

  return null
}

/** BE 正式キー paymentBusinessSalesDate（Y-m-d）。無いときは null。 */
export const resolvePostClosingBusinessSalesDate = (source: PostClosingPaymentSource): string | undefined => {
  return source.paymentBusinessSalesDate ?? undefined
}

/** 割引の締め後修正UI表示判定（利用明細と同趣旨） */
export const shouldShowPostClosingDiscountRow = (discount: FixedDiscountDetail, isPostClosingEditingRow: boolean): boolean => {
  if (isFixedDiscountDetail(discount)) return true
  return isPostClosingEditingRow
}

/** 締め後修正API用の入金額。UIの isPaid に合わせて 0 または売上金額。 */
export const resolvePostClosingSalesPaymentPrice = (
  sale: Pick<SalesType, 'isPaid' | 'salesSubjectPrice' | 'quantity' | 'salesPaymentPrice'>,
): number => {
  if (!sale.isPaid) return 0
  const computed = (Number(sale.salesSubjectPrice) || 0) * (Number(sale.quantity) || 0)
  const current = Number(sale.salesPaymentPrice)
  return current > 0 ? current : computed
}

/**
 * 精算済→未精算のとき BE が is_confirmed 解除に必要とする clearConfirmation。
 * isConfirmed=0 の対象行（isConfirm && isPaid のみ）では送らない。
 */
export const shouldClearPostClosingConfirmation = (
  originSale: Pick<SalesType, 'isPaid' | 'isConfirmed'>,
  sale: Pick<SalesType, 'isPaid'>,
): boolean => {
  return isSalesFlagEnabled(originSale.isConfirmed) && Boolean(originSale.isPaid) && !sale.isPaid
}

/** 割引: isConfirmed=1 かつ 精算済→「-」（未精算相当）のとき clearConfirmation */
export const shouldClearPostClosingDiscountConfirmation = (
  originDiscount: Pick<SalesDiscountType, 'isConfirmed' | 'salesPaymentName' | 'salesPaymentId'>,
  discount: Pick<SalesDiscountType, 'salesPaymentName' | 'salesPaymentId'>,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  return (
    isSalesFlagEnabled(originDiscount.isConfirmed) &&
    resolveDiscountPaid(originDiscount, paymentMasters) &&
    !resolveDiscountPaid(discount, paymentMasters)
  )
}

/** 割引の入金日時モーダル表示判定。未精算相当（「-」）なら出さない。 */
export const shouldShowPostClosingDiscountPaymentModal = (
  discount: Pick<SalesDiscountType, 'salesPaymentName' | 'salesPaymentId' | 'paymentAtUnixtime'>,
  paymentMasters?: DiscountPaymentMaster[],
): boolean => {
  return resolveDiscountPaid(discount, paymentMasters) && hasPostClosingPaymentDatetime(discount)
}

const omitNullishPostClosingParams = (params: PostClosingSalesAdjustmentParams): PostClosingSalesAdjustmentParams => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined),
  ) as PostClosingSalesAdjustmentParams
}

export const buildPostClosingSalesAdjustmentParams = (
  sale: SalesType,
  modalValues?: PostClosingModalSubmitValues,
  originSale?: SalesType,
): PostClosingSalesAdjustmentParams => {
  const params: PostClosingSalesAdjustmentParams = {
    ...(sale.id ? { saleId: sale.id } : {}),
    salesDate: sale.salesDate,
    salesSubjectId: sale.salesSubjectId,
    salesSubSubjectId: sale.salesSubSubjectId || '',
    salesSubjectPrice: Number(sale.salesSubjectPrice) || 0,
    quantity: Number(sale.quantity) || 0,
    salesPaymentId: sale.salesPaymentId,
    companyId: sale.companyId || '',
    salesPaymentPrice: sale.isPaid ? resolvePostClosingSalesPaymentPrice(sale) : 0,
    salesAccountsReceivableMasterId: sale.salesAccountsReceivableMasterId || '',
    reservationId: sale.reservationId,
    guestRoomAssignId: sale.guestRoomAssignId || undefined,
  }

  if (modalValues?.dateMode === 'paymentAt' && modalValues.paymentAtUnixtime) {
    params.paymentAtUnixtime = modalValues.paymentAtUnixtime
  }

  if (modalValues?.dateMode === 'salesDate' && modalValues.paymentBusinessSalesDate) {
    params.paymentBusinessSalesDate = modalValues.paymentBusinessSalesDate
  }

  if (originSale && shouldClearPostClosingConfirmation(originSale, sale)) {
    params.clearConfirmation = true
  }

  return omitNullishPostClosingParams(params)
}

export const buildPostClosingDeleteParams = (saleId: string): PostClosingSalesAdjustmentParams => ({
  saleId,
  delete: true,
})

type AdminSalesOriginItem = {
  id: string
  isConfirm?: boolean | number | null
  isConfirmed?: boolean | number | null
  isPaid?: boolean | number
  salesPaymentName?: string
  hasSentPos?: boolean
}

/** 通常保存から除外する固定行か。グレー表示と同じ条件 */
const isAdminSalesProtectedOrigin = (origin: AdminSalesOriginItem): boolean => {
  const isPaid = 'isPaid' in origin ? Boolean(origin.isPaid) : resolveDiscountPaid({ salesPaymentName: origin.salesPaymentName ?? '' })
  return isFixedSalesDetail({
    isConfirm: origin.isConfirm ?? false,
    isPaid,
    isConfirmed: origin.isConfirmed,
    hasSentPos: origin.hasSentPos,
  })
}

/** 通常保存（admin-sales）用 payload。グレー固定行は鉛筆経路専用のため除外する */
export const buildAdminSalesSavePayload = <T extends { id?: string }>(
  saveItems: T[],
  deleteItems: Array<{ id?: string }>,
  originItems: AdminSalesOriginItem[],
): Array<T | { id: string }> => {
  const originById = new Map(originItems.map(item => [item.id, item]))
  const saves = saveItems.filter(item => {
    if (!item.id) return true
    const origin = originById.get(item.id)
    return !origin || !isAdminSalesProtectedOrigin(origin)
  })
  const deletes = deleteItems.flatMap(({ id }) => {
    if (!id) return []
    const origin = originById.get(id)
    if (origin && isAdminSalesProtectedOrigin(origin)) return []
    return [{ id }]
  })
  // UUID FK の空文字は BE で保存失敗→ロールバックするため未設定扱いにする（キー省略）
  // FE専用フラグも送信しない
  return [...saves, ...deletes].map(item => {
    const next = { ...item } as T & {
      salesSubSubjectId?: string
      salesAccountsReceivableMasterId?: string
      fromAccommodationTax?: boolean
      isEdit?: boolean
    }
    if (next.salesSubSubjectId === '') {
      delete next.salesSubSubjectId
    }
    if (next.salesAccountsReceivableMasterId === '') {
      delete next.salesAccountsReceivableMasterId
    }
    delete next.fromAccommodationTax
    delete next.isEdit
    return next
  })
}

export const isPostClosingRowInputValid = (sale: SalesType): boolean => {
  if (!sale.salesSubjectId || !sale.salesPaymentId || !sale.reservationId) return false
  if (!String(sale.salesSubjectPrice).match(/^-?[0-9]+$/)) return false
  if (!String(sale.quantity).match(/^-?[0-9]+$/)) return false
  return true
}

export const isPostClosingDiscountRowInputValid = (discount: SalesDiscountType): boolean => {
  if (!discount.salesDiscountId || !discount.salesPaymentId || !discount.reservationId) return false
  if (!String(discount.salesSubjectPrice).match(/^[0-9]+$/)) return false
  return true
}

export const buildPostClosingDiscountAdjustmentParams = (
  discount: SalesDiscountType,
  modalValues?: PostClosingModalSubmitValues,
  originDiscount?: SalesDiscountType,
  paymentMasters?: DiscountPaymentMaster[],
): PostClosingSalesAdjustmentParams => {
  const isPaid = resolveDiscountPaid(discount, paymentMasters)
  const params: PostClosingSalesAdjustmentParams = {
    ...(discount.id ? { saleId: discount.id } : {}),
    salesDate: discount.salesDate,
    salesDiscountId: discount.salesDiscountId,
    salesSubjectPrice: Number(discount.salesSubjectPrice) || 0,
    salesPaymentId: discount.salesPaymentId,
    companyId: discount.companyId || '',
    salesAccountsReceivableMasterId: discount.salesAccountsReceivableMasterId || '',
    reservationId: discount.reservationId,
    guestRoomAssignId: discount.guestRoomAssignId,
  }

  if (discount.checkinId) {
    params.checkinId = discount.checkinId
  }

  // 未精算相当（「-」）のときは入金日時を送らない
  if (isPaid) {
    if (modalValues?.dateMode === 'paymentAt' && modalValues.paymentAtUnixtime) {
      params.paymentAtUnixtime = modalValues.paymentAtUnixtime
    }

    if (modalValues?.dateMode === 'salesDate' && modalValues.paymentBusinessSalesDate) {
      params.paymentBusinessSalesDate = modalValues.paymentBusinessSalesDate
    }
  }

  if (originDiscount && shouldClearPostClosingDiscountConfirmation(originDiscount, discount, paymentMasters)) {
    params.clearConfirmation = true
  }

  return omitNullishPostClosingParams(params)
}

export const POST_CLOSING_NOT_POST_CLOSING_CONTEXT_ERROR_CODE = 106
export const POST_CLOSING_NO_CHANGES_ERROR_CODE = 107
export const POST_CLOSING_CLEAR_CONFIRMATION_INVALID_ERROR_CODE = 108
export const POST_CLOSING_INVALID_PAYMENT_BUSINESS_DATE_ERROR_CODE = 109

type SalesGroupByDateEntry = {
  reservation_id?: string
  is_paid?: boolean
  sales_payment_id?: string
  company_id?: string
}

type DiscountPaymentOption = {
  value: string
  label: string
  children?: Array<{ value: string; label: string }>
}

/** 割引の対象支払い候補用。同一 reservation × 同一売上日の精算済み売上のみ対象にする */
export const collectReservationPaidSales = (
  salesGroupBySalesDate: Record<string, SalesGroupByDateEntry[]> | undefined,
  reservationId: string | undefined,
  salesDate: string | undefined,
): SalesGroupByDateEntry[] => {
  if (!salesGroupBySalesDate || !reservationId || !salesDate) return []
  return (salesGroupBySalesDate[salesDate] ?? []).filter(
    entry => entry.reservation_id === reservationId && entry.is_paid,
  )
}

export const buildDiscountPaymentMethodOptions = (
  salesDiscountPayments: PaymentSettingType[],
  salesGroupBySalesDate: Record<string, SalesGroupByDateEntry[]> | undefined,
  reservationId: string | undefined,
  salesDate: string | undefined,
  advanceCompany?: { paymentMethod?: { id?: string }; paymentCompanies?: Array<{ id?: string; name?: string }> },
): DiscountPaymentOption[] => {
  const paidSales = collectReservationPaidSales(salesGroupBySalesDate, reservationId, salesDate)

  const advanceCompanyOptions = advanceCompany?.paymentCompanies
    ?.filter(item => paidSales.some(v => v.company_id === item?.id))
    .map(item => ({ value: item?.id ?? '', label: item?.name ?? '' }))
    .filter(item => item.value)

  return salesDiscountPayments
    .map(item => {
      if (
        Object.keys(advanceCompany?.paymentMethod || {}).length &&
        item?.id === advanceCompany?.paymentMethod?.id &&
        paidSales.some(v => v.sales_payment_id === item?.id)
      ) {
        return { value: item.id!, label: item.name!, children: advanceCompanyOptions }
      }
      if (item?.name === '-') {
        return { value: item.id!, label: item.name! }
      }
      if (paidSales.some(v => v.sales_payment_id === item?.id)) {
        return { value: item.id!, label: item.name! }
      }
      return null
    })
    .filter((option): option is DiscountPaymentOption => option !== null)
}

export const buildSelectablePaymentBusinessDateSet = (items: SelectablePaymentBusinessDateItem[]): Set<string> =>
  new Set(items.filter(item => item.isConfirm || item.isNow).map(item => item.salesDate))

export const isSelectablePaymentBusinessDate = (salesDate: string, selectableDates: Set<string>): boolean => selectableDates.has(salesDate)

export function createIsOutsideSelectablePaymentBusinessDates(selectableDates: Set<string>) {
  return (day: moment.Moment): boolean => {
    return !isSelectablePaymentBusinessDate(day.format('YYYY-MM-DD'), selectableDates)
  }
}

/** isOutsideRange と同趣旨（react-dates の isDayBlocked 用） */
export function createIsDayBlockedForSelectablePaymentBusinessDates(selectableDates: Set<string>) {
  return createIsOutsideSelectablePaymentBusinessDates(selectableDates)
}

export const getPostClosingAdjustmentAlertMessage = (error: any, t: TFunction): string | null => {
  const errorCode = error?.response?.data?.errorCode
  const errorMessage = error?.response?.data?.errorMessage as string | undefined

  switch (errorCode) {
    case POST_CLOSING_NOT_POST_CLOSING_CONTEXT_ERROR_CODE:
      return t('HotelPms.PostClosing.notPostClosingContext')
    case POST_CLOSING_NO_CHANGES_ERROR_CODE:
      return errorMessage || t('HotelPms.PostClosing.noChanges')
    case POST_CLOSING_CLEAR_CONFIRMATION_INVALID_ERROR_CODE:
      return errorMessage || t('HotelPms.PostClosing.clearConfirmationInvalid')
    case POST_CLOSING_INVALID_PAYMENT_BUSINESS_DATE_ERROR_CODE:
      return errorMessage || t('HotelPms.PostClosing.invalidPaymentBusinessDate')
    default:
      return null
  }
}
